import { useEffect, useState } from "react";
import { Quote, ExternalLink, Info, AlertCircle, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { startGoogleLogin } from "@/lib/login";

// Defense-in-depth: block "First Last" name patterns even if the pipeline
// already strips them (matches the backend validator regex).
const PERSONAL_NAME_RE = /\b[A-Z][a-z]+ [A-Z][a-z]+\b/;

/** Visual identity tokens used to make Student Voices CLEARLY distinct
 *  from verified-data cards. NEVER mimic our metric-card style here. */
const UNVERIFIED_TAG = {
  bg: "#FEF3C7",   // amber-100 — community/caution coded
  fg: "#92400E",   // amber-900
  border: "#FCD34D",
};
const PANEL = {
  background: "linear-gradient(180deg,#FAF8FF 0%,#F5F3FF 100%)", // soft violet wash
  border: "1px dashed #D6D2EE",                                  // DASHED — not a verified card
};

const POLARITY_STYLES = {
  positive: { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
  concern:  { bg: "#FFFBEB", fg: "#92400E", border: "#FCD34D" },
  neutral:  { bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1" },
};

function safeQuotes(quotes) {
  return (quotes || []).filter(q => q && q.text && !PERSONAL_NAME_RE.test(q.text));
}

function dominantPolarity(s) {
  if (!s) return "mixed";
  const { positive = 0, mixed = 0, negative = 0 } = s;
  const max = Math.max(positive, mixed, negative);
  if (max === positive) return "positive";
  if (max === negative) return "negative";
  return "mixed";
}

function ageInDays(iso) {
  if (!iso) return Infinity;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return Infinity;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
}

function useStudentVoices(universityId, enabled = true) {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let cancelled = false;
    if (!universityId || !enabled) {
      setState({ loading: false, data: null });
      return;
    }
    (async () => {
      try {
        // Allow 404 to resolve as a normal response (no data) — keeps CRA's
        // dev overlay quiet while still hiding the section.
        const res = await api.get(`/student-voices/${universityId}`, {
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        });
        if (cancelled) return;
        if (res.status === 404) setState({ loading: false, data: null });
        else setState({ loading: false, data: res.data });
      } catch {
        if (!cancelled) setState({ loading: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [universityId, enabled]);
  return state;
}

// ── Persistent unverified tag (used on every instance, both variants) ─────────
function UnverifiedTag({ small }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${small ? "text-micro" : "text-micro"} font-semibold tracking-[0.05em] uppercase rounded-full`}
      style={{
        background: UNVERIFIED_TAG.bg,
        color: UNVERIFIED_TAG.fg,
        border: `1px solid ${UNVERIFIED_TAG.border}`,
        padding: small ? "2px 8px" : "4px 10px",
      }}
      data-testid="sv-unverified-tag"
    >
      <AlertCircle size={small ? 10 : 11} strokeWidth={2.4} />
      Community sentiment · unverified
    </span>
  );
}

// ── Compact line for the Decision Report ─────────────────────────────────────
export function StudentVoicesLine({ universityId }) {
  const { loading, data } = useStudentVoices(universityId);
  if (loading || !data) return null;

  const safe = safeQuotes(data.quotes);
  if ((!data.themes || data.themes.length === 0) && safe.length === 0) return null;

  const dom = dominantPolarity(data.sentiment);
  const firstConcern = (data.themes || []).find(t => t.polarity === "concern");

  return (
    <div
      className="mt-4 rounded-[var(--r12)] px-4 py-3 flex items-start gap-3"
      style={PANEL}
      data-testid="sv-line"
    >
      <UnverifiedTag small />
      <p className="text-body-sm text-[var(--s700)] leading-relaxed flex-1 num">
        Community sentiment: mostly <b className="text-[var(--s900)]">{dom}</b>{" "}
        (<span className="num">{data.post_count}</span> posts)
        {firstConcern ? <> — top concern: <b className="text-[var(--s900)]">{firstConcern.label}</b>.</> : "."}
        <span className="text-[var(--s500)] italic ml-1">Unverified · verify with the university.</span>
      </p>
    </div>
  );
}

// ── Full section for the University Detail page ──────────────────────────────
export default function StudentVoices({ universityId }) {
  const { user } = useAuth();
  const { loading, data } = useStudentVoices(universityId, !!user);

  // ───── Locked teaser for guests ─────
  if (!user) {
    const startGoogle = () => {
      startGoogleLogin(window.location.pathname + window.location.search);
    };
    return (
      <section
        className="mt-12 rounded-[var(--r20)] p-7 lg:p-9"
        style={PANEL}
        data-testid="student-voices-locked"
        aria-label="Student Voices — locked"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-h2 font-semibold text-[var(--s900)] leading-tight">
            What students actually say
          </h3>
          <UnverifiedTag />
        </div>
        <p className="mt-3 text-body-sm italic text-[var(--s600)] leading-relaxed max-w-3xl">
          Aggregated from public student discussions. Opinions, not facts — verify with the university.
        </p>

        <div className="mt-7 flex flex-col items-center text-center px-4 py-10 rounded-[var(--r16)] bg-white/60 backdrop-blur-sm border border-dashed border-[#D6D2EE]">
          <div className="w-12 h-12 rounded-full bg-[#F3EEFF] text-[#7C3AED] flex items-center justify-center mb-4">
            <Lock size={20} strokeWidth={1.8} />
          </div>
          <h4 className="text-h3 font-semibold text-[var(--s900)] leading-tight">
            Sign in to read what students actually say
          </h4>
          <p className="mt-2 text-body-sm text-[var(--s600)] leading-relaxed max-w-md">
            Themes, sentiment split, and anonymised quotes — sourced from public discussions and clearly labelled as unverified opinion.
          </p>
          <button
            onClick={startGoogle}
            data-testid="sv-unlock"
            className="btn-primary mt-5 inline-flex items-center justify-center gap-2"
          >
            Start free
          </button>
        </div>

        <p className="mt-7 pt-5 border-t border-dashed border-[#D6D2EE] text-micro italic text-[var(--s500)]">
          Sentiment is qualitative and may not reflect your experience.
        </p>
      </section>
    );
  }

  // ───── Authenticated render ─────
  if (loading || !data) return null;

  const safe = safeQuotes(data.quotes).slice(0, 4);
  const themes = data.themes || [];
  if (themes.length === 0 && safe.length === 0) return null;

  const sent = data.sentiment || { positive: 0, mixed: 0, negative: 0 };
  const isStale = ageInDays(data.last_updated) > 90;

  return (
    <section
      className="mt-12 rounded-[var(--r20)] p-7 lg:p-9"
      style={PANEL}
      data-testid="student-voices-section"
      aria-label="Student Voices — community sentiment"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-h2 font-semibold text-[var(--s900)] leading-tight">
          What students actually say
        </h3>
        <UnverifiedTag />
      </div>
      <p className="mt-3 text-body-sm italic text-[var(--s600)] leading-relaxed max-w-3xl">
        Aggregated from public student discussions. Opinions, not facts — verify with the university.
      </p>

      {/* Sentiment summary */}
      <div className="mt-7 grid lg:grid-cols-5 gap-7 items-start">
        <div className="lg:col-span-3">
          <p className="text-body text-[var(--s800)] leading-relaxed">
            {data.summary}
          </p>
          <div className="mt-4 text-micro text-[var(--s500)] num">
            Based on <b className="text-[var(--s700)] font-semibold">{data.post_count}</b> posts
            <span className="mx-1.5">·</span>
            updated <b className="text-[var(--s700)] font-semibold">{data.last_updated}</b>
            {isStale && (
              <span className="ml-2 inline-flex items-center gap-1 text-[#92400E]" data-testid="sv-stale">
                <Info size={10} strokeWidth={2.4} /> may be outdated
              </span>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2">Sentiment split</div>
          {/* 3-segment bar */}
          <div className="h-2.5 w-full rounded-full overflow-hidden flex bg-white">
            <div style={{ width: `${sent.positive}%`, background: POLARITY_STYLES.positive.fg }} aria-label={`positive ${sent.positive}%`} />
            <div style={{ width: `${sent.mixed}%`,    background: POLARITY_STYLES.neutral.fg  }} aria-label={`mixed ${sent.mixed}%`} />
            <div style={{ width: `${sent.negative}%`, background: POLARITY_STYLES.concern.fg  }} aria-label={`negative ${sent.negative}%`} />
          </div>
          <div className="mt-2.5 flex items-center justify-between text-micro num text-[var(--s600)]">
            <span className="inline-flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-sm" style={{ background: POLARITY_STYLES.positive.fg }} /> {sent.positive}% positive</span>
            <span className="inline-flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-sm" style={{ background: POLARITY_STYLES.neutral.fg }} /> {sent.mixed}% mixed</span>
            <span className="inline-flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-sm" style={{ background: POLARITY_STYLES.concern.fg }} /> {sent.negative}% negative</span>
          </div>
        </div>
      </div>

      {/* Themes */}
      {themes.length > 0 && (
        <div className="mt-7">
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-3">Themes</div>
          <div className="flex flex-wrap gap-2">
            {themes.map((t, i) => {
              const s = POLARITY_STYLES[t.polarity] || POLARITY_STYLES.neutral;
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 text-label rounded-full px-3 py-1.5"
                  style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
                >
                  <i className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.fg }} />
                  {t.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Quotes */}
      {safe.length > 0 && (
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {safe.map((q, i) => (
            <figure
              key={i}
              className="relative rounded-[var(--r12)] bg-white/70 backdrop-blur-sm border border-[#E7E3F5] px-5 py-4"
              data-testid={`sv-quote-${i}`}
            >
              <Quote
                size={20}
                strokeWidth={1.6}
                className="absolute -top-2.5 -left-2.5 text-[#A78BFA] bg-white rounded-full p-0.5 border border-[#E7E3F5]"
                aria-hidden
              />
              <blockquote className="font-display italic text-body text-[var(--s800)] leading-snug line-clamp-2">
                &ldquo;{q.text}&rdquo;
              </blockquote>
              <figcaption className="mt-2.5 flex items-center justify-between text-micro text-[var(--s500)] num">
                <span>
                  <b className="text-[var(--s700)] font-semibold">{q.platform}</b>
                  <span className="mx-1.5">·</span>
                  {q.date}
                </span>
                {q.url && (
                  <a
                    href={q.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#7C3AED] hover:text-[#5B21B6] font-semibold underline underline-offset-2 decoration-[#A78BFA] hover:decoration-[#5B21B6]"
                  >
                    view source <ExternalLink size={10} strokeWidth={2.4} />
                  </a>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {/* Footer */}
      <p className="mt-7 pt-5 border-t border-dashed border-[#D6D2EE] text-micro italic text-[var(--s500)]">
        Sentiment is qualitative and may not reflect your experience.
      </p>
    </section>
  );
}
